import React from 'react';
import './StatsCard.css';

/**
 * StatsCard Component
 *
 * Reusable stats card component with self-contained default styles.
 * Each page can optionally apply its own CSS styling via className prop.
 *
 * The component includes default styles that work across all pages.
 * Individual pages can override these styles using their own CSS.
 *
 * @param {string} icon - Icon/emoji to display
 * @param {number|string} number - Stat number/value
 * @param {string|React.ReactNode} label - Stat label text
 * @param {string} [subtext] - Optional secondary line under the label (e.g. all-time context)
 * @param {string} className - Additional CSS class name for customization (e.g., 'total', 'active')
 * @param {React.ReactNode} [aside] - Optional right-side content (keeps primary stack aligned)
 * @param {Function} [onClick] - Optional click handler; makes the card interactive (Users-style)
 * @param {string} [title] - Optional native tooltip (recommended when onClick is set)
 * @returns {React.ReactNode} Stats card component
 */
function StatsCard({
  icon,
  number,
  label,
  subtext = null,
  className = '',
  aside = null,
  onClick = null,
  title = undefined,
}) {
  const hasAside = Boolean(aside);
  const isClickable = typeof onClick === 'function';

  const primary = (
    <>
      <span className="stats-card-icon">{icon}</span>
      <div className="stats-card-content">
        <div className="stats-card-number">{number}</div>
        <div className="stats-card-label">{label}</div>
        {subtext ? <div className="stats-card-subtext">{subtext}</div> : null}
      </div>
    </>
  );

  const handleKeyDown = (event) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <div
      className={`stats-card-container ${hasAside ? 'has-aside' : ''} ${subtext ? 'has-subtext' : ''} ${isClickable ? 'clickable' : ''} ${className}`.trim()}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      title={title}
    >
      {hasAside ? (
        <>
          <div className="stats-card-primary">{primary}</div>
          <div className="stats-card-aside">{aside}</div>
        </>
      ) : (
        primary
      )}
    </div>
  );
}

export default StatsCard;
